import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

/** Montag, passend zum deutschen Kalender im Admin. */
const WEEK_OPTS = { weekStartsOn: 1 as const };

/**
 * Übersicht „um heute“: ab Wochenanfang bis zum Ende der Woche,
 * die `weeksAhead` Wochen nach heute liegt. 5 ≈ sechs Wochen, 11 ≈ zwölf.
 */
export const DASHBOARD_NEAR_WEEKS = 5;
export const DASHBOARD_HORIZON_WEEKS = 11;

/** So viele künftige Termine zeigt „Nächste Termine“. */
export const UPCOMING_TARGET = 6;

export type MsRange = { start: number; end: number };

export type CalendarViewName = 'month' | 'week' | 'day';

export function normalizeCalendarView(view: string): CalendarViewName {
  if (view === 'month' || view === 'day') return view;
  return 'week';
}

export function mergeRanges(ranges: MsRange[]): MsRange[] {
  const sorted = ranges
    .filter((range) => range.end >= range.start)
    .map((range) => ({ start: range.start, end: range.end }))
    .sort((a, b) => a.start - b.start);
  if (sorted.length === 0) return [];

  const merged: MsRange[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const next = sorted[i];
    if (next.start <= last.end + 1) {
      last.end = Math.max(last.end, next.end);
    } else {
      merged.push({ ...next });
    }
  }
  return merged;
}

/** Teile von `need`, die noch nicht in `covered` liegen. Berührende Bereiche zählen als abgedeckt. */
export function uncoveredParts(covered: MsRange[], need: MsRange): MsRange[] {
  if (need.end < need.start) return [];
  const merged = mergeRanges(covered);
  const missing: MsRange[] = [];
  let cursor = need.start;

  for (const range of merged) {
    if (range.end < cursor) continue;
    if (range.start > need.end) break;
    if (range.start > cursor) {
      missing.push({ start: cursor, end: range.start - 1 });
    }
    cursor = Math.max(cursor, range.end + 1);
    if (cursor > need.end) return missing;
  }

  if (cursor <= need.end) missing.push({ start: cursor, end: need.end });
  return missing;
}

/**
 * Ende des zusammenhängend geladenen Bereichs, der `fromMs` enthält.
 * Eine Lücke davor liefert null — dann dürfen spätere, schon geladene Termine
 * nicht als „nächste“ durch diese Lücke hindurch angezeigt werden.
 */
export function contiguousCoverageEnd(covered: MsRange[], fromMs: number): number | null {
  for (const range of mergeRanges(covered)) {
    if (range.end < fromMs) continue;
    if (range.start > fromMs) return null;
    return range.end;
  }
  return null;
}

export function rangeAroundToday(now: Date, weeksAhead: number): MsRange {
  const start = startOfWeek(now, WEEK_OPTS);
  const end = endOfWeek(addWeeks(now, weeksAhead), WEEK_OPTS);
  return { start: start.getTime(), end: end.getTime() };
}

/** Genau das Raster, das Monat, Woche oder Tag zeigen — inklusive der angrenzenden Tage im Monatsraster. */
export function visibleCalendarRange(view: CalendarViewName, cursor: Date): MsRange {
  if (view === 'month') {
    const start = startOfWeek(startOfMonth(cursor), WEEK_OPTS);
    const end = endOfWeek(endOfMonth(cursor), WEEK_OPTS);
    return { start: start.getTime(), end: end.getTime() };
  }
  if (view === 'week') {
    const start = startOfWeek(cursor, WEEK_OPTS);
    const end = endOfWeek(cursor, WEEK_OPTS);
    return { start: start.getTime(), end: end.getTime() };
  }
  const start = startOfDay(cursor);
  const end = endOfDay(cursor);
  return { start: start.getTime(), end: end.getTime() };
}

/**
 * Sichtbarer Bereich plus der vorherige und der nächste Schritt,
 * jeweils inklusive ihres eigenen Rasters. Blättern um eins ist damit schon geladen.
 */
export function bufferedCalendarRange(view: CalendarViewName, cursor: Date): MsRange {
  if (view === 'month') {
    const start = startOfWeek(startOfMonth(subMonths(cursor, 1)), WEEK_OPTS);
    const end = endOfWeek(endOfMonth(addMonths(cursor, 1)), WEEK_OPTS);
    return { start: start.getTime(), end: end.getTime() };
  }
  if (view === 'week') {
    const start = startOfWeek(subWeeks(cursor, 1), WEEK_OPTS);
    const end = endOfWeek(addWeeks(cursor, 1), WEEK_OPTS);
    return { start: start.getTime(), end: end.getTime() };
  }
  const start = startOfDay(subDays(cursor, 1));
  const end = endOfDay(addDays(cursor, 1));
  return { start: start.getTime(), end: end.getTime() };
}

export function countStartsInWindow(startsMs: number[], nowMs: number, window: MsRange): number {
  return startsMs.filter((start) => start >= nowMs && start >= window.start && start <= window.end).length;
}

export function mergeById<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  const byId = new Map<string, T>();
  for (const item of existing) byId.set(item.id, item);
  for (const item of incoming) byId.set(item.id, item);
  return Array.from(byId.values());
}
